import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import PauseCircleIcon from '@mui/icons-material/PauseCircle'
import DownloadIcon from '../../shared_components/DownloadIcon'

import {useAudioPlayer} from './AudioPlayerContext'
import FileUploadActions from '../../shared_actions/FileUploadActions'
import MixpanelLogger from '../../lib/MixpanelLogger'
import {formatTime} from './audioUtils'
import {fileExtension} from '../forms/v2/lib/tools'

import './audioPlayerBar.scss'

const DOWNLOAD_ERROR_TYPES = ['unplayable', 'browser']

const RETRYABLE_ERROR_TYPES = ['load', 'network']

const AudioPlayerBar = () => {
  const {currentTrack, currentTime, duration, error, isLoading, isPlaying, retry, seek, togglePlayPause} = useAudioPlayer()

  if (!currentTrack) return null

  const handleDownload = () => {
    MixpanelLogger.track('click_attachment_download', {file_type: fileExtension(currentTrack.name || '')})

    FileUploadActions.downloadFile(currentTrack.id, currentTrack.name)
  }

  if (error) {
    const renderErrorAction = () => {
      if (DOWNLOAD_ERROR_TYPES.includes(error.type)) {
        return (
          <Button className='retry-button' onClick={handleDownload} size='small' variant='text'>
            Download
          </Button>
        )
      }

      if (RETRYABLE_ERROR_TYPES.includes(error.type)) {
        return (
          <Button className='retry-button' disabled={isLoading} onClick={retry} size='small' variant='text'>
            {isLoading ? 'Retrying…' : 'Retry'}
          </Button>
        )
      }

      return null
    }

    return (
      <div className='audio-player-bar audio-player-bar--error' role='alert'>
        <span className='error-message'>{error.message}</span>
        {renderErrorAction()}
      </div>
    )
  }

  const handleProgressClick = event => {
    if (!duration) return

    const bar = event.currentTarget
    const rect = bar.getBoundingClientRect()
    const clickPosition = (event.clientX - rect.left) / rect.width
    const newTime = clickPosition * duration

    seek(newTime)
  }

  const handleProgressKeyDown = event => {
    if (!duration) return

    const seekStep = 5
    let newTime = null

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        newTime = Math.min(currentTime + seekStep, duration)
        break
      case 'ArrowLeft':
      case 'ArrowDown':
        newTime = Math.max(currentTime - seekStep, 0)
        break
      case 'Home':
        newTime = 0
        break
      case 'End':
        newTime = duration
        break
      default:
        return
    }

    event.preventDefault()
    seek(newTime)
  }

  const progress = duration ? (currentTime / duration) * 100 : 0

  const playPauseLabel = () => {
    if (isLoading) return 'Loading'
    if (isPlaying) return 'Pause'
    return 'Play'
  }

  const renderPlayPauseIcon = () => {
    if (isLoading) return <span className='loading-spinner' />
    if (isPlaying) return <PauseCircleIcon />
    return <PlayCircleIcon />
  }

  return (
    <div className='audio-player-bar' role='region' aria-label='Audio player'>
      <IconButton
        aria-label={playPauseLabel()}
        className='play-pause-button'
        disabled={isLoading}
        onClick={togglePlayPause}
        size='small'>
        {renderPlayPauseIcon()}
      </IconButton>
      <div className='track-info'>
        <span className='track-name'>{currentTrack.name.replace(/\.[^/.]+$/, '')}</span>
        <div className='progress-section'>
          <span className='time-display'>{formatTime(currentTime)}</span>
          <div
            aria-label='Seek'
            aria-valuemax={Math.floor(duration)}
            aria-valuemin={0}
            aria-valuenow={Math.floor(currentTime)}
            aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
            className='progress-bar-container'
            onClick={handleProgressClick}
            onKeyDown={handleProgressKeyDown}
            role='slider'
            tabIndex={0}>
            <div className='progress-bar-track'>
              <div className='progress-bar-fill' style={{width: `${progress}%`}} />
            </div>
          </div>
          <span className='time-display'>{formatTime(duration)}</span>
        </div>
      </div>
      <IconButton
        aria-label={`Download ${currentTrack.name}`}
        className='download-button'
        onClick={handleDownload}
        size='small'>
        <DownloadIcon />
      </IconButton>
    </div>
  )
}

export default AudioPlayerBar
